/**
 * CosmosChat.jsx - The Oracle Astro-Coach Interface
 * 
 * A professional astrology coaching chat with:
 * - 20 prompts per day limit (invisible counter)
 * - Auto-save on back button
 * - Session continuity (remembers yesterday)
 * - Image Upload (chart images)
 * - Voice Input (Gemini Speech-to-Text)
 * - Memory Tab: View past summaries & manifest visions
 */
import { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Mic, MicOff, Upload, ArrowLeft, Sparkles, Stars, AlertCircle, BookOpen, Moon, Loader2, ScrollText, MessageCircle, Scroll, ThumbsUp, ThumbsDown } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { useGeminiVoiceInput } from "../hooks/useGeminiVoiceInput";
import OracleMemoryTab from "./OracleMemoryTab";
import { getAuthHeaders } from "../utils/api";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// ============================================
// MESSAGE BUBBLE COMPONENT
// ============================================
const MessageBubble = ({ message, onRate = null, showRating = true }) => {
    const isUser = message.role === 'user';
    const [hasRated, setHasRated] = useState(message.user_rating || null);
    
    // Parse special formatting in oracle messages
    const renderContent = (content) => {
        if (isUser) return content;
        
        // Handle bold text and section headers
        const parts = content.split(/(\*\*[^*]+\*\*)/g);
        return parts.map((part, i) => {
            if (part.startsWith('**') && part.endsWith('**')) {
                return <strong key={i} className="text-blue-300">{part.slice(2, -2)}</strong>;
            }
            return part;
        });
    };
    
    const handleRate = async (rating) => {
        if (hasRated || !onRate) return;
        setHasRated(rating);
        await onRate(message.id || message.timestamp, rating);
    };
    
    return (
        <motion.div
            className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-4`}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
        >
            <div className="max-w-[85%]">
                <div
                    className={`rounded-2xl px-4 py-3 ${isUser ? 'rounded-br-md' : 'rounded-bl-md'}`}
                    style={{
                        background: isUser
                            ? 'linear-gradient(135deg, rgba(30, 58, 138, 0.6) 0%, rgba(30, 64, 175, 0.4) 100%)'
                            : 'linear-gradient(135deg, rgba(30, 58, 138, 0.2) 0%, rgba(15, 23, 42, 0.4) 100%)',
                        border: `1px solid ${isUser ? 'rgba(59, 130, 246, 0.4)' : 'rgba(59, 130, 246, 0.2)'}`,
                    }}
                >
                    {message.imagePreview && (
                        <img 
                            src={message.imagePreview} 
                            alt="Chart" 
                            className="max-h-40 rounded-lg mb-2 opacity-90"
                        />
                    )}
                    <p className="text-sm font-inter text-white/90 whitespace-pre-wrap leading-relaxed">
                        {renderContent(message.content)}
                    </p>
                    <span className="text-[10px] text-white/30 mt-2 block">
                        {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                </div>
                
                {/* Customer rating for Oracle messages */}
                {!isUser && showRating && onRate && (
                    <motion.div 
                        className="flex items-center justify-end gap-1 mt-1 pr-2"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3 }}
                    >
                        {hasRated ? (
                            <span className="text-[10px] text-white/30 flex items-center gap-1">
                                {hasRated === 'helpful' ? '👍' : '👎'} 
                                <span>Thanks for your feedback</span>
                            </span>
                        ) : (
                            <>
                                <span className="text-[10px] text-white/30 mr-1">Helpful?</span>
                                <button
                                    onClick={() => handleRate('helpful')}
                                    className="p-1 hover:bg-green-500/20 rounded transition-colors group"
                                    title="This was helpful"
                                    data-testid="rate-helpful"
                                >
                                    <ThumbsUp className="w-3 h-3 text-white/30 group-hover:text-green-400" />
                                </button>
                                <button
                                    onClick={() => handleRate('not_helpful')}
                                    className="p-1 hover:bg-red-500/20 rounded transition-colors group"
                                    title="This wasn't helpful"
                                    data-testid="rate-not-helpful"
                                >
                                    <ThumbsDown className="w-3 h-3 text-white/30 group-hover:text-red-400" />
                                </button>
                            </>
                        )}
                    </motion.div>
                )}
            </div>
        </motion.div>
    );
};

// ============================================
// TYPING INDICATOR
// ============================================
const TypingIndicator = () => (
    <motion.div
        className="flex justify-start mb-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
    >
        <div
            className="rounded-2xl rounded-bl-md px-5 py-4"
            style={{
                background: 'linear-gradient(135deg, rgba(30, 58, 138, 0.2) 0%, rgba(15, 23, 42, 0.4) 100%)',
                border: '1px solid rgba(59, 130, 246, 0.2)',
            }}
        >
            <div className="flex items-center gap-2">
                <Stars size={14} className="text-blue-400 animate-pulse" />
                <div className="flex gap-1">
                    {[0, 1, 2].map(i => (
                        <motion.div
                            key={i}
                            className="w-2 h-2 rounded-full bg-blue-400"
                            animate={{ opacity: [0.3, 1, 0.3] }}
                            transition={{ duration: 1, repeat: Infinity, delay: i * 0.2 }}
                        />
                    ))}
                </div>
            </div>
        </div>
    </motion.div>
);

// ============================================
// SESSION CLOSED OVERLAY
// ============================================
const SessionClosedOverlay = ({ onSaveToJournal, hasSaved, t }) => (
    <motion.div
        className="absolute inset-0 z-20 flex items-center justify-center"
        style={{ background: 'rgba(8, 12, 28, 0.95)' }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
    >
        <div className="text-center px-8 max-w-md">
            <Moon size={48} className="text-blue-400 mx-auto mb-4" />
            <h3 className="font-cinzel text-xl text-white mb-3">
                {t('chat.portal_closes')}
            </h3>
            <p className="text-sm text-white/60 mb-6 leading-relaxed">
                {t('chat.session_limit_reached')}
            </p>
            {!hasSaved && (
                <button
                    onClick={onSaveToJournal}
                    className="flex items-center gap-2 mx-auto px-6 py-3 rounded-xl text-sm font-medium transition-all hover:scale-105"
                    style={{
                        background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(30, 58, 138, 0.4) 100%)',
                        border: '1px solid rgba(59, 130, 246, 0.4)',
                        color: '#60A5FA'
                    }}
                    data-testid="cosmos-save-journal"
                >
                    <BookOpen size={16} />
                    {t('chat.save_to_journal')}
                </button>
            )}
            {hasSaved && (
                <p className="text-sm text-green-400 flex items-center justify-center gap-2">
                    <Sparkles size={14} />
                    {t('chat.saved_to_journal')}
                </p>
            )}
        </div>
    </motion.div>
);

// ============================================
// MAIN COSMOS CHAT COMPONENT
// ============================================
export const CosmosChat = ({ onClose, initialQuery = '' }) => {
    const { t, language } = useLanguage();
    const [messages, setMessages] = useState([]);
    const [inputText, setInputText] = useState(initialQuery); // Pre-fill if provided
    const [isLoading, setIsLoading] = useState(false);
    const [uploadedImage, setUploadedImage] = useState(null);
    const [imageError, setImageError] = useState(null);
    const [sessionStatus, setSessionStatus] = useState({ prompt_count: 0, is_locked: false });
    const [isSessionClosed, setIsSessionClosed] = useState(false);
    const [hasSavedToJournal, setHasSavedToJournal] = useState(false);
    const [activeTab, setActiveTab] = useState('chat'); // 'chat' or 'memory'
    const [isSealingRevelation, setIsSealingRevelation] = useState(false);
    const [sealedMemoryId, setSealedMemoryId] = useState(null);
    const chatContainerRef = useRef(null);
    const inputRef = useRef(null);
    const fileInputRef = useRef(null);
    const hasInitialized = useRef(false);
    
    // Minimum message length for Oracle
    const MIN_MESSAGE_LENGTH = 15;
    
    // Oracle persona nudge messages (when input is too short)
    const ORACLE_NUDGE = {
        en: "A single star does not make a galaxy. Share more of your cosmic inquiry so I may align the ephemeris with your path.",
        es: "Una sola estrella no hace una galaxia. Comparte más sobre tu consulta cósmica para que pueda alinear las efemérides con tu camino."
    };
    
    // Language-aware labels
    const isSpanish = language?.startsWith('es');
    
    // Focus input when opening with pre-filled query
    useEffect(() => {
        if (initialQuery && inputRef.current) {
            inputRef.current.focus();
        }
    }, [initialQuery]);
    
    // Gemini Voice Input Hook - Professional quality transcription
    const { 
        isRecording, 
        isTranscribing, 
        transcript, 
        error: voiceError, 
        isSupported: voiceSupported, 
        recordingTime,
        maxRecordingTime,
        toggleRecording, 
        clearTranscript,
        formatTime,
        languageDisplay,
        waveformData,
        languageIcon
    } = useGeminiVoiceInput(language);

    // Get user name from localStorage
    const getUserName = () => {
        try {
            const onboardingData = localStorage.getItem('astral_belonging_onboarding');
            if (onboardingData) {
                const parsed = JSON.parse(onboardingData);
                return parsed?.userData?.name || '';
            }
        } catch (e) {}
        return '';
    };

    // Check session status on mount (but DON'T load history - each visit is fresh)
    useEffect(() => {
        const checkSessionStatus = async () => {
            try {
                const headers = await getAuthHeaders();
                const statusResponse = await fetch(`${API_URL}/api/oracle/cosmos/session-status`, {
                    credentials: 'include',
                    headers
                });
                
                if (statusResponse.ok) {
                    const status = await statusResponse.json();
                    setSessionStatus(status);
                    
                    if (status.is_locked) {
                        setIsSessionClosed(true);
                    }
                }
            } catch (err) {
                console.error("Session status check failed:", err);
            }
        };
        
        checkSessionStatus();
    }, []);

    // Opening message - fetch from new Oracle API
    useEffect(() => {
        const fetchOracleGreeting = async () => {
            if (messages.length === 0 && !hasInitialized.current && !sessionStatus.is_locked) {
                hasInitialized.current = true;
                
                try {
                    // Fetch branded greeting from Oracle API
                    const langParam = language.startsWith('es') ? 'es' : 'en';
                    const headers = await getAuthHeaders();
                    const response = await fetch(`${API_URL}/api/oracle/greeting?lang=${langParam}`, {
                        method: 'POST',
                        headers: { ...headers, 'Content-Type': 'application/json' },
                        credentials: 'include',
                        body: JSON.stringify({ oracle_type: 'cosmos' })
                    });
                    
                    if (response.ok) {
                        const data = await response.json();
                        setMessages([{
                            role: 'oracle',
                            content: data.greeting,
                            timestamp: new Date().toISOString()
                        }]);
                        return;
                    }
                } catch (e) {
                    console.error("Failed to fetch Oracle greeting:", e);
                }
                
                // Fallback to local greeting
                const userName = getUserName();
                const nameGreeting = userName ? `Hi ${userName}` : 'Hi Traveler';
                const fallbackGreeting = `${nameGreeting}, welcome to the Cosmic Observatory. I am the Oracle, your astro-coach. How can the cosmos help you today?`;
                
                setMessages([{
                    role: 'oracle',
                    content: fallbackGreeting,
                    timestamp: new Date().toISOString()
                }]);
            }
        };
        
        fetchOracleGreeting();
    }, [language, sessionStatus, messages.length]);

    // Update input with voice transcript (Gemini STT)
    useEffect(() => {
        if (transcript) {
            setInputText(prev => (prev ? prev + " " + transcript : transcript).trim());
            clearTranscript();
        }
    }, [transcript, clearTranscript]);

    // Auto-scroll
    useEffect(() => {
        if (chatContainerRef.current) {
            chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
    }, [messages, isLoading]);

    // Focus input
    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    // Auto-save on unmount (back button) - REMOVED: Now handled only in handleBack()
    // This was causing duplicate memory entries

    // Handle back button with auto-save AND Two-Pass Summarization
    // CRITICAL: Close immediately, save in background (fire-and-forget)
    const handleBack = async () => {
        // Fire-and-forget: Start save in background, close immediately
        if (messages.length > 2) {
            const langParam = language.startsWith('es') ? 'es' : 'en';
            
            // Get auth headers for the request
            const headers = await getAuthHeaders();
            
            // Background save to Memory Pool - ONLY call this one
            fetch(`${API_URL}/api/oracle/session-end?lang=${langParam}`, {
                method: 'POST',
                headers: { 
                    ...headers,
                    'Content-Type': 'application/json' 
                },
                credentials: 'include',
                body: JSON.stringify({
                    oracle_type: 'cosmos',
                    history: messages.map(m => ({ role: m.role, content: m.content }))
                })
            }).catch(e => console.log('Session end save (background):', e.message));
            
            // NOTE: Removed /api/cosmos/save-session call - it was causing duplicate memory entries
        }
        
        // CRITICAL: Close immediately - no waiting
        onClose();
    };

    // Seal This Revelation - Save conversation and switch to Memory tab
    const handleSealRevelation = async () => {
        if (messages.length < 2 || isSealingRevelation) return;
        
        setIsSealingRevelation(true);
        
        try {
            const langParam = language.startsWith('es') ? 'es' : 'en';
            const headers = await getAuthHeaders();
            
            const response = await fetch(`${API_URL}/api/oracle/session-end?lang=${langParam}`, {
                method: 'POST',
                headers: { 
                    ...headers,
                    'Content-Type': 'application/json' 
                },
                credentials: 'include',
                body: JSON.stringify({
                    oracle_type: 'cosmos',
                    history: messages.map(m => ({ role: m.role, content: m.content }))
                })
            });
            
            if (response.ok) {
                const data = await response.json();
                
                // Store the new memory ID to highlight it
                if (data.insight_id) {
                    setSealedMemoryId(data.insight_id);
                }
                
                // Clear current conversation for fresh start
                setMessages([]);
                setSessionStatus({ prompt_count: 0 });
                setIsSessionClosed(false);
                setHasSavedToJournal(false);
                
                // CRITICAL: Reset initialization flag so a new greeting will be fetched
                hasInitialized.current = false;
                
                // Switch to Memory tab to show the new summary
                setActiveTab('memory');
            }
        } catch (e) {
            console.error("Seal revelation failed:", e);
        } finally {
            setIsSealingRevelation(false);
        }
    };
    
    // Rate Oracle response (customer feedback)
    const handleRateResponse = async (messageId, rating) => {
        try {
            const headers = await getAuthHeaders();
            await fetch(`${API_URL}/api/oracle/rate`, {
                method: 'POST',
                headers: { ...headers, 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    message_id: messageId,
                    oracle_type: 'cosmos',
                    rating: rating
                })
            });
        } catch (e) {
            console.error("Rating failed:", e);
        }
    };

    // Save to journal
    const handleSaveToJournal = async () => {
        try {
            const response = await fetch(`${API_URL}/api/cosmos/save-session`, {
                method: 'POST',
                credentials: 'include'
            });
            if (response.ok) {
                setHasSavedToJournal(true);
            }
        } catch (e) {
            console.error("Save to journal failed:", e);
        }
    };

    // Handle image upload
    const handleImageUpload = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        
        setImageError(null);
        
        if (file.size > 4 * 1024 * 1024) {
            setImageError(t('chat.image_too_large'));
            return;
        }
        
        const reader = new FileReader();
        reader.onload = (event) => {
            setUploadedImage(event.target?.result);
        };
        reader.readAsDataURL(file);
    };

    // Handle send
    const handleSend = async () => {
        const text = inputText.trim();
        
        if (!text && !uploadedImage) return;
        if (isLoading || isSessionClosed) return;
        
        // Oracle persona nudge - if message too short, Oracle responds in character
        if (!uploadedImage && text.length < MIN_MESSAGE_LENGTH) {
            // Add the user's short message to chat
            setMessages(prev => [...prev, {
                role: 'user',
                content: text,
                timestamp: new Date().toISOString()
            }]);
            setInputText("");
            
            // Oracle responds with a nudge (doesn't count against limit)
            setTimeout(() => {
                setMessages(prev => [...prev, {
                    role: 'oracle',
                    content: isSpanish ? ORACLE_NUDGE.es : ORACLE_NUDGE.en,
                    timestamp: new Date().toISOString(),
                    isNudge: true // Flag so we know this doesn't count
                }]);
            }, 500); // Small delay for natural feel
            
            return;
        }

        const userMessage = {
            role: 'user',
            content: text || t('chat.chart_shared'),
            imagePreview: uploadedImage,
            timestamp: new Date().toISOString()
        };

        setMessages(prev => [...prev, userMessage]);
        setInputText("");
        setIsLoading(true);

        try {
            // Prepare image data
            let imageData = null;
            if (uploadedImage) {
                imageData = uploadedImage.includes(',') 
                    ? uploadedImage.split(',')[1] 
                    : uploadedImage;
            }
            
            const headers = await getAuthHeaders();
            const response = await fetch(`${API_URL}/api/oracle/chat`, {
                method: "POST",
                headers: { ...headers, "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({
                    message: text || "Analyze this chart",
                    image_base64: imageData,
                    language: language,
                    user_name: getUserName(),
                    oracle_type: "cosmos"
                })
            });

            if (!response.ok) {
                throw new Error("Failed to get response");
            }

            const data = await response.json();
            
            setMessages(prev => [...prev, {
                role: 'oracle',
                content: data.message,
                timestamp: new Date().toISOString()
            }]);
            
            // Update session status
            setSessionStatus(prev => ({
                ...prev,
                prompt_count: data.prompt_number
            }));
            
            // Check if session is closing or locked
            if (data.is_closing || data.is_locked) {
                setIsSessionClosed(true);
            }
            
            setUploadedImage(null);

        } catch (err) {
            console.error("Cosmos Chat error:", err);
            setMessages(prev => [...prev, {
                role: 'oracle',
                content: t('chat.stars_silent'),
                timestamp: new Date().toISOString()
            }]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    return (
        <motion.div
            className="fixed inset-0 z-50 flex flex-col"
            style={{ 
                background: 'linear-gradient(180deg, rgba(15, 23, 42, 0.98) 0%, rgba(8, 12, 28, 0.99) 100%)'
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
        >
            {/* Session Closed Overlay */}
            <AnimatePresence>
                {isSessionClosed && (
                    <SessionClosedOverlay 
                        onSaveToJournal={handleSaveToJournal}
                        hasSaved={hasSavedToJournal}
                        t={t}
                    />
                )}
            </AnimatePresence>

            {/* Header with Tabs */}
            <div 
                className="flex-shrink-0 border-b"
                style={{ borderColor: 'rgba(59, 130, 246, 0.2)' }}
            >
                {/* Top row: Back button and Title */}
                <div className="p-4 pb-2 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={handleBack}
                            className="w-10 h-10 rounded-full flex items-center justify-center text-blue-400/60 hover:text-blue-400 hover:bg-blue-500/10 transition-colors"
                            data-testid="cosmos-back"
                        >
                            <ArrowLeft size={20} />
                        </button>
                        <div>
                            <h2 
                                className="font-cinzel text-xl"
                                style={{ 
                                    background: 'linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)',
                                    WebkitBackgroundClip: 'text', 
                                    WebkitTextFillColor: 'transparent' 
                                }}
                            >
                                {t('chat.the_oracle')}
                            </h2>
                            <p 
                                className="text-[10px] font-inter uppercase tracking-widest"
                                style={{ color: 'rgba(59, 130, 246, 0.5)' }}
                            >
                                {t('chat.astro_coach')}
                            </p>
                        </div>
                    </div>
                    <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center"
                        style={{ 
                            background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(30, 58, 138, 0.3) 100%)',
                            border: '1px solid rgba(59, 130, 246, 0.3)'
                        }}
                    >
                        <Stars size={18} className="text-blue-400" />
                    </div>
                </div>
                
                {/* Tab Buttons */}
                <div className="flex px-4 pb-2 gap-2">
                    <button
                        onClick={() => setActiveTab('chat')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium transition-all ${
                            activeTab === 'chat'
                                ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                                : 'text-blue-400/50 hover:text-blue-400/70 hover:bg-blue-500/10'
                        }`}
                    >
                        <MessageCircle size={14} />
                        {isSpanish ? 'Conversación' : 'Conversation'}
                    </button>
                    <button
                        onClick={() => setActiveTab('memory')}
                        className={`flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium transition-all ${
                            activeTab === 'memory'
                                ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                                : 'text-blue-400/50 hover:text-blue-400/70 hover:bg-blue-500/10'
                        }`}
                    >
                        <ScrollText size={14} />
                        {isSpanish ? 'Memorias' : 'Memories'}
                    </button>
                </div>
            </div>

            {/* Content Area - Chat or Memory Tab */}
            {activeTab === 'memory' ? (
                <OracleMemoryTab oracleType="cosmos" onClose={() => setActiveTab('chat')} />
            ) : (
                <>
                    {/* Messages */}
                    <div 
                        ref={chatContainerRef}
                        className="flex-1 overflow-y-auto p-4"
                    >
                        {messages.map((msg, idx) => (
                            <MessageBubble 
                                key={idx} 
                                message={msg} 
                                onRate={handleRateResponse}
                                showRating={msg.role === 'assistant' && idx > 0}  // Only rate Oracle responses, not greeting
                            />
                        ))}
                        {/* Recording indicator shown inline when recording */}
                        {isRecording && (
                            <div className="text-sm text-red-400/60 italic mb-2 px-2">
                                🎙️ Recording...
                            </div>
                        )}
                        {isLoading && <TypingIndicator />}
                    </div>

            {/* Image Preview */}
            {uploadedImage && !isSessionClosed && (
                <div className="px-4 py-2 border-t" style={{ borderColor: 'rgba(59, 130, 246, 0.1)' }}>
                    <div className="relative inline-block">
                        <img src={uploadedImage} alt="Chart preview" className="h-16 rounded-lg opacity-80" />
                        <button
                            onClick={() => setUploadedImage(null)}
                            className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white text-xs"
                            data-testid="cosmos-remove-image"
                        >
                            ×
                        </button>
                    </div>
                </div>
            )}

            {/* Image Error */}
            {imageError && (
                <div className="mx-4 mb-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30">
                    <p className="text-xs text-red-400 flex items-center gap-2">
                        <AlertCircle size={12} />
                        {imageError}
                    </p>
                </div>
            )}

            {/* Input Area */}
            {!isSessionClosed && (
                <div 
                    className="flex-shrink-0 p-4 border-t"
                    style={{ borderColor: 'rgba(59, 130, 246, 0.2)' }}
                >
                    {/* Seal This Revelation Button - Shows after 2+ messages */}
                    {messages.length >= 2 && (
                        <motion.button
                            onClick={handleSealRevelation}
                            disabled={isSealingRevelation}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="w-full mb-3 py-2.5 px-4 rounded-xl flex items-center justify-center gap-2 transition-all"
                            style={{
                                background: isSealingRevelation 
                                    ? 'rgba(212, 175, 55, 0.1)' 
                                    : 'linear-gradient(135deg, rgba(212, 175, 55, 0.15) 0%, rgba(212, 175, 55, 0.05) 100%)',
                                border: '1px solid rgba(212, 175, 55, 0.3)',
                            }}
                            whileHover={!isSealingRevelation ? { scale: 1.01, borderColor: 'rgba(212, 175, 55, 0.5)' } : {}}
                            whileTap={!isSealingRevelation ? { scale: 0.99 } : {}}
                            data-testid="cosmos-seal-revelation"
                        >
                            {isSealingRevelation ? (
                                <>
                                    <Loader2 size={16} className="text-primary animate-spin" />
                                    <span className="text-primary/80 text-sm font-medium">
                                        {isSpanish ? 'Tejiendo tu revelación en el tapiz cósmico...' : 'Weaving your revelation into the cosmic tapestry...'}
                                    </span>
                                </>
                            ) : (
                                <>
                                    <Scroll size={16} className="text-primary" />
                                    <span className="text-primary text-sm font-medium">
                                        {isSpanish ? 'Sellar Esta Revelación' : 'Seal This Revelation'}
                                    </span>
                                    <span className="text-primary/50 text-xs">
                                        {isSpanish ? '• Guardar y crear imagen' : '• Save & create image'}
                                    </span>
                                </>
                            )}
                        </motion.button>
                    )}
                    
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleImageUpload}
                        accept="image/*"
                        className="hidden"
                        data-testid="cosmos-file-input"
                    />
                    
                    {/* Recording Indicator */}
                    <AnimatePresence>
                        {(isRecording || isTranscribing) && (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 10 }}
                                className="flex items-center justify-center gap-3 mb-3 p-2 rounded-lg"
                                style={{ background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.3)' }}
                            >
                                {isRecording ? (
                                    <>
                                        {/* Waveform Visualizer - CSS-based for performance */}
                                        <div className="flex items-center gap-0.5 h-6">
                                            {waveformData.length > 0 ? (
                                                waveformData.map((level, i) => (
                                                    <div
                                                        key={i}
                                                        className="w-1 bg-red-400 rounded-full transition-all duration-75"
                                                        style={{ 
                                                            height: `${Math.max(4, level * 0.24)}px`,
                                                            minHeight: '4px'
                                                        }}
                                                    />
                                                ))
                                            ) : (
                                                // Fallback pulsing dot if no waveform
                                                <motion.div
                                                    className="w-3 h-3 rounded-full bg-red-500"
                                                    animate={{ scale: [1, 1.3, 1], opacity: [1, 0.7, 1] }}
                                                    transition={{ duration: 1, repeat: Infinity }}
                                                />
                                            )}
                                        </div>
                                        <span className="text-red-400 text-sm font-medium">
                                            {formatTime(recordingTime)} / {formatTime(maxRecordingTime)}
                                        </span>
                                        <span className="text-red-300/80 text-sm">{languageIcon}</span>
                                    </>
                                ) : (
                                    <>
                                        <Loader2 size={16} className="text-blue-400 animate-spin" />
                                        <span className="text-blue-400 text-sm">{t('chat.transcribing') || 'Transcribing...'}</span>
                                    </>
                                )}
                            </motion.div>
                        )}
                    </AnimatePresence>
                    
                    {/* Voice Error */}
                    {voiceError && (
                        <div className="flex items-center gap-2 mb-3 p-2 rounded-lg text-red-400 text-sm" style={{ background: 'rgba(239, 68, 68, 0.1)' }}>
                            <AlertCircle size={14} />
                            {voiceError}
                        </div>
                    )}
                    
                    <div 
                        className="flex items-end gap-2 rounded-xl p-2"
                        style={{
                            background: 'rgba(0, 0, 0, 0.3)',
                            border: '1px solid rgba(59, 130, 246, 0.2)',
                        }}
                    >
                        {/* Voice Button - Gemini Speech-to-Text */}
                        {voiceSupported && (
                            <motion.button
                                onClick={toggleRecording}
                                disabled={isTranscribing}
                                className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                                    isRecording ? 'bg-red-500' : isTranscribing ? 'bg-blue-900/30' : 'bg-blue-900/50 hover:bg-blue-900/70'
                                }`}
                                style={{ 
                                    border: isRecording 
                                        ? '2px solid rgba(255, 100, 100, 0.8)' 
                                        : '1px solid rgba(59, 130, 246, 0.4)',
                                    boxShadow: isRecording ? '0 0 15px rgba(239, 68, 68, 0.5)' : 'none'
                                }}
                                whileHover={{ scale: isTranscribing ? 1 : 1.05 }}
                                whileTap={{ scale: isTranscribing ? 1 : 0.95 }}
                                animate={isRecording ? { scale: [1, 1.05, 1] } : {}}
                                transition={isRecording ? { duration: 1, repeat: Infinity } : {}}
                                data-testid="cosmos-voice-btn"
                            >
                                {isTranscribing ? (
                                    <Loader2 size={18} className="text-blue-400 animate-spin" />
                                ) : isRecording ? (
                                    <MicOff size={18} className="text-white" />
                                ) : (
                                    <Mic size={18} className="text-blue-400" />
                                )}
                            </motion.button>
                        )}

                        {/* Photo Button */}
                        <motion.button
                            onClick={() => fileInputRef.current?.click()}
                            className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-blue-900/50 hover:bg-blue-900/70 transition-colors"
                            style={{ border: '1px solid rgba(59, 130, 246, 0.4)' }}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            data-testid="cosmos-photo-btn"
                        >
                            <Upload size={18} className="text-blue-400" />
                        </motion.button>

                        {/* Text Input */}
                        <textarea
                            ref={inputRef}
                            value={inputText}
                            onChange={(e) => setInputText(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder={isRecording 
                                ? (t('chat.recording') || 'Recording...') 
                                : isTranscribing 
                                ? (t('chat.transcribing') || 'Transcribing...') 
                                : t('chat.cosmos_placeholder')}
                            className="flex-1 bg-transparent text-white/90 text-sm font-inter resize-none placeholder:text-white/30 focus:outline-none"
                            style={{ minHeight: '40px', maxHeight: '120px' }}
                            rows={1}
                            disabled={isRecording || isTranscribing}
                            data-testid="cosmos-input"
                        />

                        {/* Send Button */}
                        <motion.button
                            onClick={handleSend}
                            disabled={(!inputText.trim() && !uploadedImage) || isLoading}
                            className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center disabled:opacity-30 transition-all"
                            style={{
                                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(30, 58, 138, 0.2) 100%)',
                                border: '1px solid rgba(59, 130, 246, 0.4)',
                            }}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            data-testid="cosmos-send"
                        >
                            <Send size={18} className="text-blue-400" />
                        </motion.button>
                    </div>
                    <p className="text-[10px] text-center text-white/30 mt-2">
                        {t('chat.cosmos_hint')}
                    </p>
                </div>
            )}
                </>
            )}
        </motion.div>
    );
};

export default CosmosChat;
